import { useCallback, useRef } from 'react';
import {
  Carousel,
  CarouselSlide,
  CarouselSlideProps,
  useCarousel,
} from '@/components/carousel';
import { FlashcardNavProps } from './types';

export type FlaschardThumbProps = {
  seen?: boolean;
  slideIndex: number;
  displayIndex?: number;
  onSelect?: () => void;
};

export type FlashcardThumbsNavProps = React.ComponentProps<'div'> &
  FlashcardNavProps & {
    slots?: {
      ThumbSlide?: React.ElementType<FlaschardThumbProps>;
    };
  };

export function FlashcardThumbsNav({
  onNavigateTo = () => {},
  displayIndex,
  disabled,
  deck,
  slots = {},
  className,
  ...others
}: FlashcardThumbsNavProps) {
  const { ThumbSlide = FlashcardThumbSlide } = slots;

  const [carouselRef] = useCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    displayIndex,
  });

  const seenRef = useRef<number[]>([]);

  const handleClickThumb = useCallback(
    (index: number) => {
      if (!disabled) onNavigateTo(index);
    },
    [disabled, onNavigateTo]
  );

  if (displayIndex && !seenRef.current.includes(displayIndex)) {
    seenRef.current.push(displayIndex);
  }

  return (
    <Carousel {...others} spacing={null} asChild ref={carouselRef}>
      <div className="mm:-ml-1 mm:*:pl-1">
        {deck.content.map((flashcard, i) => {
          return (
            <ThumbSlide
              displayIndex={displayIndex}
              slideIndex={i}
              key={flashcard.id}
              onSelect={() => {
                handleClickThumb(i);
              }}
              seen={seenRef.current.includes(i)}
            />
          );
        })}
      </div>
    </Carousel>
  );
}
FlashcardThumbsNav.displayName = 'FlashcardThumbsNav';

export function FlashcardThumbSlide(
  props: Omit<CarouselSlideProps, 'asChild'> & FlaschardThumbProps
) {
  const {
    seen,
    slideIndex,
    displayIndex,
    onSelect = () => {},
    ...others
  } = props;

  const current = displayIndex === slideIndex;

  return (
    <CarouselSlide className="mm:basis-auto" {...others}>
      <button
        className="mm:rounded-xl mm:border-2 mm:bg-card mm:text-muted-foreground/50 mm:size-10 mm:cursor-pointer mm:transition-colors mm:aria-selected:text-foreground mm:aria-selected:border-foreground"
        onClick={() => {
          onSelect();
        }}
        type="button"
        data-state={seen ? 'seen' : 'unseen'}
        // accessibility
        aria-selected={current}
      >
        {slideIndex + 1}
      </button>
    </CarouselSlide>
  );
}
FlashcardThumbSlide.displayName = 'FlashcardThumbSlide';
